import { createIcons, icons } from "lucide";

import { renderCharts } from "./charts";
import { loadPartnerIntakeUrl } from "./config";
import {
  buildKpis,
  chooseRoute,
  freshnessLabels,
  formatNumber,
  formatPercent,
  scoreBand
} from "./metrics";
import { computeScenarioExposure, weightToPercent } from "./scenario";
import { TOOLTIP_DEFINITIONS } from "./tooltips";
import type { DashboardData, RouteMetric, ScenarioExposure, ScenarioOverride, TooltipDefinition } from "./types";

let disposeCharts: (() => void) | null = null;
let cursorSignalMounted = false;
let tooltipMounted = false;
let activeTooltipTrigger: HTMLElement | null = null;
let tooltipPinned = false;
let tooltipHovered = false;
let tooltipCloseTimer: number | null = null;

const TOOLTIP_ID = "dashboard-tooltip";
const DETAILS_PANEL_IDS = ["terminals", "signal", "route"] as const;
type DetailsPanelId = (typeof DETAILS_PANEL_IDS)[number];

function element(id: string): HTMLElement {
  const found = document.getElementById(id);
  if (found === null) {
    throw new Error(`Missing element #${id}`);
  }
  return found;
}

function selectElement(id: string): HTMLSelectElement {
  const found = document.getElementById(id);
  if (!(found instanceof HTMLSelectElement)) {
    throw new Error(`Missing select #${id}`);
  }
  return found;
}

function buttonElement(id: string): HTMLButtonElement {
  const found = document.getElementById(id);
  if (!(found instanceof HTMLButtonElement)) {
    throw new Error(`Missing button #${id}`);
  }
  return found;
}

function optionalElement(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function optionalInputElement(id: string): HTMLInputElement | null {
  const found = document.getElementById(id);
  return found instanceof HTMLInputElement ? found : null;
}

function optionalButtonElement(id: string): HTMLButtonElement | null {
  const found = document.getElementById(id);
  return found instanceof HTMLButtonElement ? found : null;
}

function isDetailsPanelId(value: string | undefined): value is DetailsPanelId {
  return DETAILS_PANEL_IDS.includes(value as DetailsPanelId);
}

function setText(id: string, value: string): void {
  element(id).textContent = value;
}

function setAuthCover(visible: boolean): void {
  element("auth-cover").classList.toggle("is-hidden", !visible);
}

function setScoreRing(score: number | null): void {
  const circle = document.getElementById("score-ring");
  if (!(circle instanceof SVGCircleElement)) {
    return;
  }
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const normalized = score === null ? 0 : Math.min(Math.max(score, 0), 100);
  circle.style.strokeDasharray = `${circumference}`;
  circle.style.strokeDashoffset = `${circumference - (normalized / 100) * circumference}`;
}

function clearTooltipCloseTimer(): void {
  if (tooltipCloseTimer !== null) {
    window.clearTimeout(tooltipCloseTimer);
    tooltipCloseTimer = null;
  }
}

function scheduleTooltipClose(): void {
  clearTooltipCloseTimer();
  tooltipCloseTimer = window.setTimeout(() => {
    closeTooltip();
  }, 140);
}

function scoreHeatColor(score: number | null): string {
  if (score === null || !Number.isFinite(score)) {
    return "#fcd34d";
  }
  const stops: Array<{ score: number; rgb: [number, number, number] }> = [
    { score: 0, rgb: [34, 197, 94] },
    { score: 50, rgb: [250, 204, 21] },
    { score: 75, rgb: [249, 115, 22] },
    { score: 100, rgb: [239, 68, 68] }
  ];
  const normalized = Math.min(Math.max(score, 0), 100);
  const foundUpperIndex = stops.findIndex((stop) => normalized <= stop.score);
  const upperIndex = foundUpperIndex === -1 ? stops.length - 1 : foundUpperIndex;
  const upper = stops[upperIndex]!;
  const lower = stops[Math.max(upperIndex - 1, 0)]!;
  const span = upper.score - lower.score || 1;
  const ratio = (normalized - lower.score) / span;
  const interpolate = (index: 0 | 1 | 2): number =>
    Math.round(lower.rgb[index] + (upper.rgb[index] - lower.rgb[index]) * ratio);
  const red = interpolate(0);
  const green = interpolate(1);
  const blue = interpolate(2);
  return `rgb(${red}, ${green}, ${blue})`;
}

function setScoreHeat(score: number | null): void {
  optionalElement("score-band")?.style.setProperty("--score-color", scoreHeatColor(score));
}

function setScoreStatusPill(route: RouteMetric | null): void {
  const pill = optionalElement("score-status-pill");
  if (pill === null) {
    return;
  }
  const status = route?.scoreStatus.toUpperCase() ?? "PROVISIONAL";
  const isStale = status === "STALE" || route?.congestionScore === null;
  pill.className = isStale ? "scope-pill stale" : "scope-pill";
  pill.textContent = isStale ? "Stale" : "Provisional";
}

function tooltipButtonMarkup(tooltipId: string, label: string): string {
  return `
    <button class="info-button" type="button" data-tooltip="${tooltipId}" aria-label="${label}">
      <i data-lucide="info" aria-hidden="true"></i>
    </button>
  `;
}

function categoryClass(category: string): string {
  const normalized = category.toLowerCase().replace(/\s+/g, "-");
  return normalized || "unknown";
}

function kpiMarkup(route: RouteMetric | null): string {
  return buildKpis(route)
    .map(
      (kpi) => `
        <article class="kpi-card">
          <div class="kpi-card-head">
            <div class="kpi-icon"><i data-lucide="${kpi.icon}" aria-hidden="true"></i></div>
            ${tooltipButtonMarkup(`kpi-${kpi.id}`, `About ${kpi.label}`)}
          </div>
          <span>${kpi.label}</span>
          <strong>${kpi.value}</strong>
          <small>${kpi.caption}</small>
        </article>
      `
    )
    .join("");
}

function terminalMarkup(route: RouteMetric | null): string {
  if (route === null) {
    return `<div class="empty-state">No terminal data</div>`;
  }
  return route.terminalOccupancy
    .map((terminal) => {
      const pct = Math.min(Math.max(terminal.percent ?? 0, 0), 100);
      return `
        <div class="terminal-row">
          <div class="terminal-row-top">
            <span>${terminal.label}</span>
            <strong>${formatPercent(terminal.percent)}</strong>
          </div>
          <div class="terminal-track" aria-hidden="true"><span style="width:${pct}%"></span></div>
          <small>${formatNumber(terminal.vessels)} berth-adjacent vessels</small>
        </div>
      `;
    })
    .join("");
}

function renderRouteSelect(routes: RouteMetric[], selectedRouteId: string | null): string | null {
  const select = selectElement("route-select");
  const current = chooseRoute(routes, selectedRouteId);
  select.innerHTML = routes
    .map((route) => `<option value="${route.routeId}">${route.routeName}</option>`)
    .join("");
  if (current !== null) {
    select.value = current.routeId;
  }
  return current?.routeId ?? null;
}

function setScenarioInputState(disabled: boolean, exposure: ScenarioExposure): void {
  const slider = optionalInputElement("singapore-weight-slider");
  const input = optionalInputElement("singapore-weight-input");
  const weightPercent = weightToPercent(exposure.activeWeight);
  if (slider !== null) {
    slider.disabled = disabled;
    slider.value = String(weightPercent ?? 0);
    slider.setAttribute("aria-valuetext", weightPercent === null ? "No scenario weight" : `${weightPercent}%`);
  }
  if (input !== null) {
    input.disabled = disabled;
    input.value = weightPercent === null ? "" : String(weightPercent);
  }
  optionalButtonElement("scenario-reset-button")?.toggleAttribute("disabled", !exposure.isOverride);
  optionalButtonElement("copy-scenario-link-button")?.toggleAttribute("disabled", disabled && !exposure.isOverride);
}

function setScenarioText(route: RouteMetric | null, exposure: ScenarioExposure): void {
  const activeWeightPercent = weightToPercent(exposure.activeWeight);
  const baselineWeightPercent = weightToPercent(exposure.baselineWeight);
  optionalElement("singapore-weight-display")?.replaceChildren(
    activeWeightPercent === null ? "-" : `${formatNumber(activeWeightPercent)}%`
  );
  optionalElement("scenario-weight-baseline")?.replaceChildren(
    baselineWeightPercent === null ? "-" : `${formatNumber(baselineWeightPercent)}%`
  );
  optionalElement("scenario-weight-current")?.replaceChildren(
    activeWeightPercent === null ? "-" : `${formatNumber(activeWeightPercent)}%`
  );
  optionalElement("baseline-route-score")?.replaceChildren(formatNumber(exposure.baselineScore, 1));
  optionalElement("scenario-route-score")?.replaceChildren(formatNumber(exposure.score, 1));
  optionalElement("route-score-label")?.replaceChildren(exposure.isOverride ? "What-if Exposure" : "Route Exposure");
  optionalElement("scenario-status")?.replaceChildren(
    route === null
      ? "Sign in to load ArcGIS route rows before customizing."
      : exposure.score === null
        ? "Live M10 is stale or unavailable, so the what-if score is withheld."
        : exposure.isOverride
          ? "What-if scenario only. ArcGIS data and M10 remain unchanged."
          : "Using the ArcGIS default route weight for this demo lane."
  );
}

function renderScenarioLab(route: RouteMetric | null, exposure: ScenarioExposure): void {
  const canCustomize =
    route !== null &&
    route.congestionScore !== null &&
    route.scoreStatus.toUpperCase() !== "STALE" &&
    exposure.activeWeight !== null;
  const modeBadge = optionalElement("scenario-mode-badge");
  if (modeBadge !== null) {
    modeBadge.toggleAttribute("hidden", !exposure.isOverride);
  }
  setScenarioInputState(!canCustomize, exposure);
  setScenarioText(route, exposure);
}

function renderRoute(route: RouteMetric | null, exposure: ScenarioExposure): void {
  setText("m10-score", route?.congestionScore === null || route === null ? "-" : formatNumber(route.congestionScore, 1));
  setText("m10-category", route === null ? "No data" : route.congestionCategory);
  setScoreRing(route?.congestionScore ?? null);
  setScoreHeat(route?.congestionScore ?? null);
  setScoreStatusPill(route);

  element("kpi-grid").innerHTML = kpiMarkup(route);
  setText("route-score", formatNumber(exposure.score, 1));
  const category = exposure.category;
  const categoryNode = element("route-category");
  categoryNode.textContent = category;
  categoryNode.className = `exposure-badge ${categoryClass(category)}`;
  setText("route-lane", route === null ? "-" : `${route.origin} -> ${route.transshipmentPort} -> ${route.destination}`);
  setText("route-cargo", route?.cargoArchetype ?? "-");
  setText("route-driver", route?.topDriver ?? "-");
  setText("route-action", exposure.recommendedAction);
  element("terminal-list").innerHTML = terminalMarkup(route);
  renderScenarioLab(route, exposure);
  createIcons({ icons });
}

export function setAuthState(signedIn: boolean): void {
  const authButton = buttonElement("auth-button");
  const coverAuthButton = buttonElement("cover-auth-button");
  const label = element("auth-button-label");
  authButton.disabled = false;
  coverAuthButton.disabled = false;
  label.textContent = signedIn ? "Sign out" : "Sign in";
  authButton.querySelector("i")?.setAttribute("data-lucide", signedIn ? "log-out" : "log-in");
  coverAuthButton.textContent = "Sign in";
  coverAuthButton.insertAdjacentHTML("afterbegin", '<i data-lucide="log-in" aria-hidden="true"></i>');
  setAuthCover(!signedIn);
  createIcons({ icons });
}

export function setAuthPrompt(title: string, message: string): void {
  optionalElement("auth-cover-title")?.replaceChildren(title);
  optionalElement("auth-cover-message")?.replaceChildren(message);
}

export function setAuthUnavailable(message: string): void {
  const authButton = buttonElement("auth-button");
  const coverAuthButton = buttonElement("cover-auth-button");
  authButton.disabled = true;
  coverAuthButton.disabled = true;
  element("auth-button-label").textContent = "Config required";
  optionalElement("auth-cover-title")?.replaceChildren("Configuration Required");
  optionalElement("auth-cover-message")?.replaceChildren(message);
  setAuthCover(true);
  createIcons({ icons });
}

export function setBusy(busy: boolean): void {
  buttonElement("refresh-button").disabled = busy;
  element("app").classList.toggle("is-loading", busy);
}

export function setError(message: string): void {
  const pill = optionalElement("score-status-pill");
  if (pill !== null) {
    pill.className = "scope-pill stale";
    pill.textContent = "Attention";
  }
  setText("metrics-refresh", message);
  setText("vessel-refresh", "Vessel freshness unavailable");
}

export function setScenarioLinkStatus(message: string): void {
  optionalElement("scenario-link-status")?.replaceChildren(message);
}

export function renderDashboard(
  data: DashboardData,
  selectedRouteId: string | null,
  scenarioOverride: ScenarioOverride
): string | null {
  const activeRouteId = renderRouteSelect(data.routes, selectedRouteId);
  const route = chooseRoute(data.routes, activeRouteId);
  const exposure = computeScenarioExposure(route, scenarioOverride);
  renderRoute(route, exposure);
  const freshness = freshnessLabels(route?.calcTimestamp ?? null, data.vesselPositionTimestamp);
  setText("metrics-refresh", freshness.metrics);
  setText("vessel-refresh", freshness.vessels);
  setText("vessel-count", `Vessels: ${formatNumber(data.vesselCount)}`);
  setText("terminal-count", `Terminals: ${formatNumber(data.terminalCount)}`);

  if (disposeCharts !== null) {
    disposeCharts();
  }
  disposeCharts = renderCharts(data.trends);
  return activeRouteId;
}

function setDetailsPanelCollapsed(collapsed: boolean): void {
  const grid = optionalElement("dashboard-grid");
  const panel = optionalElement("metrics-panel");
  const button = optionalButtonElement("details-toggle-button");
  if (grid === null || panel === null || button === null) {
    return;
  }

  grid.classList.toggle("details-collapsed", collapsed);
  panel.toggleAttribute("hidden", collapsed);
  button.setAttribute("aria-expanded", String(!collapsed));
  button.setAttribute("aria-label", collapsed ? "Show details panel" : "Hide details panel");
  button.querySelector("i")?.setAttribute("data-lucide", collapsed ? "panel-right-open" : "panel-right-close");
  createIcons({ icons });
}

function setActiveDetailsPanel(panelId: DetailsPanelId): void {
  document.querySelectorAll<HTMLButtonElement>("[data-details-tab]").forEach((tab) => {
    const isActive = tab.dataset.detailsTab === panelId;
    tab.classList.toggle("is-active", isActive);
    tab.setAttribute("aria-selected", String(isActive));
    tab.tabIndex = isActive ? 0 : -1;
  });

  document.querySelectorAll<HTMLElement>("[data-details-panel]").forEach((panel) => {
    const isActive = panel.dataset.detailsPanel === panelId;
    panel.classList.toggle("is-active", isActive);
    panel.toggleAttribute("hidden", !isActive);
  });
}

export function wireDashboardPanelControls(): void {
  const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-details-tab]"));
  if (tabs.length === 0) {
    return;
  }

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const panelId = tab.dataset.detailsTab;
      if (isDetailsPanelId(panelId)) {
        setActiveDetailsPanel(panelId);
        setDetailsPanelCollapsed(false);
      }
    });

    tab.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        return;
      }
      event.preventDefault();
      const currentIndex = tabs.indexOf(tab);
      const nextIndex =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? tabs.length - 1
            : event.key === "ArrowLeft"
              ? (currentIndex - 1 + tabs.length) % tabs.length
              : (currentIndex + 1) % tabs.length;
      tabs[nextIndex]?.focus();
      tabs[nextIndex]?.click();
    });
  });

  optionalButtonElement("details-toggle-button")?.addEventListener("click", () => {
    const grid = optionalElement("dashboard-grid");
    const collapsed = grid?.classList.contains("details-collapsed") ?? false;
    setDetailsPanelCollapsed(!collapsed);
  });

  setActiveDetailsPanel("terminals");
}

export function wireStaticIcons(): void {
  createIcons({ icons });
}

function partnerUrlWithSource(url: string, sourcePage: string | undefined): string {
  if (!sourcePage) {
    return url;
  }
  const parsed = new URL(url);
  parsed.searchParams.set("field:source_page", sourcePage);
  return parsed.toString();
}

export function hydratePartnerIntakeLinks(partnerIntakeUrl: string | undefined): void {
  if (!partnerIntakeUrl) {
    return;
  }

  document.querySelectorAll<HTMLAnchorElement>("[data-partner-intake-link]").forEach((link) => {
    link.href = partnerUrlWithSource(partnerIntakeUrl, link.dataset.partnerSource);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  });
}

export async function wirePartnerIntakeLinks(): Promise<void> {
  try {
    hydratePartnerIntakeLinks(await loadPartnerIntakeUrl());
  } catch {
    hydratePartnerIntakeLinks(undefined);
  }
}

export function wireCursorSignal(): void {
  if (
    cursorSignalMounted ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    window.matchMedia("(pointer: coarse)").matches
  ) {
    return;
  }

  const body = document.body;
  let signal = document.querySelector<HTMLElement>(".cursor-signal");
  if (signal === null) {
    signal = document.createElement("div");
    signal.className = "cursor-signal";
    signal.setAttribute("aria-hidden", "true");
    body.append(signal);
  }

  const isTouchPointer = (event: PointerEvent): boolean => event.pointerType === "touch";

  const moveCursor = (event: PointerEvent): void => {
    if (isTouchPointer(event)) {
      return;
    }
    body.style.setProperty("--cursor-x", `${event.clientX}px`);
    body.style.setProperty("--cursor-y", `${event.clientY}px`);
    body.classList.add("cursor-live");
  };

  const pressCursor = (event: PointerEvent): void => {
    if (!isTouchPointer(event)) {
      body.classList.add("cursor-press");
    }
  };

  const releaseCursor = (): void => {
    body.classList.remove("cursor-press");
  };

  const clearCursor = (): void => {
    body.classList.remove("cursor-live", "cursor-press");
  };

  window.addEventListener("pointermove", moveCursor, { passive: true });
  window.addEventListener("pointerdown", pressCursor, { passive: true });
  window.addEventListener("pointerup", releaseCursor, { passive: true });
  window.addEventListener("pointercancel", clearCursor, { passive: true });
  window.addEventListener("blur", clearCursor);
  document.documentElement.addEventListener("pointerleave", clearCursor, { passive: true });
  cursorSignalMounted = true;
}

function tooltipTrigger(target: EventTarget | null): HTMLElement | null {
  return target instanceof HTMLElement ? target.closest<HTMLElement>("[data-tooltip]") : null;
}

function tooltipDefinition(trigger: HTMLElement): TooltipDefinition | null {
  const tooltipId = trigger.dataset.tooltip;
  return tooltipId === undefined ? null : TOOLTIP_DEFINITIONS[tooltipId] ?? null;
}

function ensureTooltip(): HTMLElement {
  let tooltip = document.getElementById(TOOLTIP_ID);
  if (tooltip === null) {
    tooltip = document.createElement("div");
    tooltip.id = TOOLTIP_ID;
    tooltip.className = "tooltip-popover";
    tooltip.setAttribute("role", "tooltip");
    tooltip.setAttribute("hidden", "");
    tooltip.addEventListener("pointerenter", () => {
      tooltipHovered = true;
      clearTooltipCloseTimer();
    });
    tooltip.addEventListener("pointerleave", () => {
      tooltipHovered = false;
      scheduleTooltipClose();
    });
    document.body.append(tooltip);
  }
  return tooltip;
}

function positionTooltip(tooltip: HTMLElement, trigger: HTMLElement): void {
  const triggerBox = trigger.getBoundingClientRect();
  const tooltipBox = tooltip.getBoundingClientRect();
  const left = Math.min(
    Math.max(triggerBox.left + triggerBox.width / 2 - tooltipBox.width / 2, 12),
    window.innerWidth - tooltipBox.width - 12
  );
  const top =
    triggerBox.top > tooltipBox.height + 18
      ? triggerBox.top - tooltipBox.height - 10
      : triggerBox.bottom + 10;
  tooltip.style.left = `${left + window.scrollX}px`;
  tooltip.style.top = `${Math.max(top + window.scrollY, 12)}px`;
}

function openTooltip(trigger: HTMLElement, pinned = false): void {
  const definition = tooltipDefinition(trigger);
  if (definition === null) {
    return;
  }
  clearTooltipCloseTimer();
  const tooltip = ensureTooltip();
  const title = document.createElement("strong");
  title.textContent = definition.title;

  const body = document.createElement("span");
  body.textContent = definition.body;

  tooltip.replaceChildren(title, body);
  if (definition.formula !== undefined) {
    const formula = document.createElement("code");
    formula.textContent = definition.formula;
    tooltip.append(formula);
  }
  if (definition.href !== undefined) {
    const link = document.createElement("a");
    link.href = definition.href;
    link.textContent = definition.linkLabel ?? "Learn More";
    tooltip.append(link);
  }
  tooltip.removeAttribute("hidden");
  activeTooltipTrigger?.removeAttribute("aria-describedby");
  trigger.setAttribute("aria-describedby", TOOLTIP_ID);
  activeTooltipTrigger = trigger;
  tooltipPinned = pinned;
  positionTooltip(tooltip, trigger);
}

function closeTooltip(force = false): void {
  clearTooltipCloseTimer();
  if ((tooltipPinned || tooltipHovered) && !force) {
    return;
  }
  const tooltip = document.getElementById(TOOLTIP_ID);
  tooltip?.setAttribute("hidden", "");
  activeTooltipTrigger?.removeAttribute("aria-describedby");
  activeTooltipTrigger = null;
  tooltipPinned = false;
}

function targetInsideTooltip(target: EventTarget | null): boolean {
  const tooltip = document.getElementById(TOOLTIP_ID);
  return target instanceof Node && tooltip !== null && tooltip.contains(target);
}

export function wireTooltips(): void {
  if (tooltipMounted) {
    return;
  }

  document.addEventListener("pointerover", (event) => {
    const trigger = tooltipTrigger(event.target);
    if (trigger !== null) {
      openTooltip(trigger);
    }
  });

  document.addEventListener("pointerout", (event) => {
    const trigger = tooltipTrigger(event.target);
    if (trigger !== null) {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && (trigger.contains(relatedTarget) || targetInsideTooltip(relatedTarget))) {
        return;
      }
      scheduleTooltipClose();
    }
  });

  document.addEventListener("focusin", (event) => {
    const trigger = tooltipTrigger(event.target);
    if (trigger !== null) {
      openTooltip(trigger);
    }
  });

  document.addEventListener("focusout", (event) => {
    if (tooltipTrigger(event.target) !== null) {
      if (targetInsideTooltip(event.relatedTarget)) {
        return;
      }
      scheduleTooltipClose();
    }
  });

  document.addEventListener("click", (event) => {
    const trigger = tooltipTrigger(event.target);
    if (trigger !== null) {
      event.preventDefault();
      const alreadyPinned = activeTooltipTrigger === trigger && tooltipPinned;
      if (alreadyPinned) {
        closeTooltip(true);
      } else {
        openTooltip(trigger, true);
      }
      return;
    }
    if (targetInsideTooltip(event.target)) {
      return;
    }
    closeTooltip(true);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeTooltip(true);
    }
  });

  window.addEventListener("resize", () => {
    if (activeTooltipTrigger !== null && !ensureTooltip().hasAttribute("hidden")) {
      positionTooltip(ensureTooltip(), activeTooltipTrigger);
    }
  });

  tooltipMounted = true;
}

export function wireNavigationMenu(): void {
  const button = document.querySelector<HTMLButtonElement>("[data-menu-button]");
  const menu = document.querySelector<HTMLElement>("[data-menu]");
  if (button === null || menu === null) {
    return;
  }

  const setOpen = (open: boolean): void => {
    button.setAttribute("aria-expanded", String(open));
    button.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    menu.toggleAttribute("hidden", !open);
  };

  button.addEventListener("click", () => {
    setOpen(button.getAttribute("aria-expanded") !== "true");
  });

  menu.querySelectorAll<HTMLAnchorElement>("a").forEach((link) => {
    link.addEventListener("click", () => {
      setOpen(false);
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      setOpen(false);
    }
  });

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Node && !button.contains(target) && !menu.contains(target)) {
      setOpen(false);
    }
  });
}
